import {
  // Checkbox,
  FormControl,
  InputBase,
  InputLabel as MuiInputLabel,
  ListItemText,
  MenuItem,
  Select as MuiSelect,
  styled,
  useTheme,
  Typography,
  ListSubheader,
  InputAdornment,
  Box,
  SelectChangeEvent,
} from '@mui/material';
import {useMediaQuery} from '@mui/material';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  useDeferredValue,
  startTransition,
} from 'react';
import type {SelectProps, OptionType} from '..';
import {ChevronDown, SearchIcon} from '@jobseeker/lotus/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {Search} from '@jobseeker/lotus/icons';
import TextField from '@jobseeker/lotus/elements/Input';

const ImageV2 = styled(Image)(({theme}) => ({
  userSelect: 'none',
  padding: '2px',
  marginRight: theme.spacing(1.7),
  marginLeft: theme.spacing(1.7),
  marginTop: theme.spacing(0.4),
  transition: '0.3s ease-in-out',
  [theme.breakpoints.between('md', 'lg')]: {
    // marginRight: 1.5,
  },
}));

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

// Options are mounted all at once when the menu opens. Long lists (locations is
// ~164 entries) make that a 300ms+ interaction on mobile, so only the first
// chunk is rendered synchronously and the tail is filled in once the menu has
// painted. 24 rows is well past the visible area of the tallest menu.
const OPTION_CHUNK = 24;

const scheduleIdle = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, {timeout: 300});
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(callback, 0);
  return () => window.clearTimeout(id);
};

export const RoundedInput: any = styled(InputBase)(({theme, placeholder}) => ({
  'label + &': {
    paddingRight: {lg: 5},
  },
  '& .MuiInputBase-input': {
    borderRadius: '100px',
    position: 'relative',
    backgroundColor: isAppIimjobs ? '#fff' : '#fff',
    color:
      placeholder || !(process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs')
        ? '#7e7e7e'
        : '#333',
    fontSize: process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs' ? 14 : 17,
    fontWeight: '500',
    lineHeight: '21px',
    padding: theme.spacing(1, 1.5),
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: '100px',
      borderColor: '#33333390',
    },
    '&:hover': {
      borderRadius: '100px',
      borderColor: '#33333390',
    },
  },
}));

const InputLabel = styled(MuiInputLabel)(({theme}) => ({
  color: '#000',
  fontSize: theme.typography.pxToRem(14),
  background: '#fff',
  lineHeight: theme.typography.pxToRem(24),
  fontWeight: 500,
  paddingRight: 2,
  width: '60%',
  zIndex: 4,
}));

export const SelectIcon = ({className, open, sx}: any) => {
  const theme = useTheme();
  return (
    <ImageV2
      className={className}
      src={ChevronDown}
      alt={'Drop Down Arrow'}
      width={12}
      height={12}
      style={{
        zIndex: 0,
        transition: 'none',
        ...sx,
      }}
      objectFit="cover"
    />
  );
};
const CHECKBOX_SIZE = isAppIimjobs ? 12 : 16;

const UncheckedSvg = (
  <svg
    width={CHECKBOX_SIZE}
    height={CHECKBOX_SIZE}
    viewBox="0 0 12 12"
    fill="none"
  >
    <rect
      x="0.5"
      y="0.5"
      width={11}
      height={11}
      rx="1.5"
      stroke="#7E7E7E"
      strokeOpacity="0.4"
    />
  </svg>
);

const CheckedIimSvg = (
  <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
    <path
      d="M0 2C0 0.895431 0.895431 0 2 0H10C11.1046 0 12 0.895431 12 2V10C12 11.1046 11.1046 12 10 12H2C0.895431 12 0 11.1046 0 10V2Z"
      fill="#49966E"
    />
    <path
      d="M0.5 2C0.5 1.17157 1.17157 0.5 2 0.5H10C10.8284 0.5 11.5 1.17157 11.5 2V10C11.5 10.8284 10.8284 11.5 10 11.5H2C1.17157 11.5 0.5 10.8284 0.5 10V2Z"
      stroke="#7E7E7E"
      strokeOpacity="0.2"
    />
    <path
      d="M8.66658 4L4.99992 7.66667L3.33325 6"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CheckedHiristSvg = (
  <svg
    width={16}
    height={16}
    viewBox="0 0 20 18"
    fill="none"
    preserveAspectRatio="xMidYMid meet"
  >
    <rect stroke="#DADADA" x={1} y={1} width={16} height={16} rx={3} />
    <path
      d="M5.16153846,7.92307692 C5.05384615,7.81538462 5,7.65384615 5,7.54615385 C5,7.43846154 5.05384615,7.27692308 5.16153846,7.16923077 L5.91538462,6.41538462 C6.13076923,6.2 6.45384615,6.2 6.66923077,6.41538462 L6.72307692,6.46923077 L9.68461538,9.64615385 C9.79230769,9.75384615 9.95384615,9.75384615 10.0615385,9.64615385 L17.2769231,2.16153846 L17.3307692,2.16153846 L17.3307692,2.16153846 C17.5461538,1.94615385 17.8692308,1.94615385 18.0846154,2.16153846 L18.8384615,2.91538462 C19.0538462,3.13076923 19.0538462,3.45384615 18.8384615,3.66923077 L18.8384615,3.66923077 L10.2230769,12.6076923 C10.1153846,12.7153846 10.0076923,12.7692308 9.84615385,12.7692308 C9.68461538,12.7692308 9.57692308,12.7153846 9.46923077,12.6076923 L5.26923077,8.08461538 L5.16153846,7.92307692 Z"
      fill="#FF6A3D"
      fillRule="nonzero"
    />
  </svg>
);

export const Checkbox = ({checked}: any) => {
  if (!checked) return UncheckedSvg;
  return isAppIimjobs ? CheckedIimSvg : CheckedHiristSvg;
};

// A MenuItem is a ButtonBase, which costs ~50 hooks and a ripple surface per
// row. Options are plain rows - they never ripple and never take focus except
// through MenuList's arrow-key handling - so this renders the same <li> MenuItem
// would, with MenuItem's box model, and none of the button machinery.
const OptionRoot = styled('li')(({theme}) => ({
  ...theme.typography.body1,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  position: 'relative',
  boxSizing: 'border-box',
  minHeight: 48,
  padding: '6px 16px',
  margin: 0,
  border: 0,
  borderRadius: 0,
  outline: 0,
  backgroundColor: 'transparent',
  color: 'inherit',
  textDecoration: 'none',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
  fontSize: 'var(--lotus-opt-fs)',
  marginTop: 'var(--lotus-opt-my)',
  marginBottom: 'var(--lotus-opt-my)',
  '&:hover, &[data-selected]': {
    backgroundColor: 'var(--lotus-opt-hover)',
  },
  '&[data-selected] *': {
    fontWeight: 500,
  },
  '&[aria-disabled="true"]': {
    opacity: theme.palette.action.disabledOpacity,
    pointerEvents: 'none',
    cursor: 'default',
  },
}));

interface OptionItemProps {
  // MUI's SelectInput/MenuList inject these onto each child.
  selected?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  value?: unknown;
  [key: string]: any;
}

const OptionItem = ({
  selected,
  disabled,
  autoFocus,
  // SelectInput reads `value` off the element to build `data-value`, then blanks
  // it in the clone. Keep it off the DOM either way.
  value: _value,
  ...rest
}: OptionItemProps) => {
  const rowRef = useRef<HTMLLIElement | null>(null);

  // <li autofocus> is inert in the DOM, so MenuList's autoFocusItem (how a
  // single-select puts the caret on the current option) has to be honoured here.
  useEffect(() => {
    if (autoFocus) rowRef.current?.focus();
  }, [autoFocus]);

  return (
    <OptionRoot
      ref={rowRef}
      // moveFocus only visits elements that carry a tabindex attribute.
      tabIndex={disabled ? undefined : -1}
      data-selected={selected || undefined}
      aria-disabled={disabled || undefined}
      {...rest}
    />
  );
};

const OptionLabel = styled('p')({
  margin: 0,
  fontSize: 'var(--lotus-opt-fs)',
  color: 'var(--lotus-opt-color)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontWeight: 400,
});

const Select: any = styled(MuiSelect)(({theme, variant}: any): any => ({
  '.MuiPaper-elevation': {
    boxShadow: 'none',
  },
  '.MuiInputBase-input': {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
}));

export default (props: SelectProps) => {
  const SelectValue: any = props?.value ?? [];
  const ref = useRef();

  const rootTheme = useTheme();
  const isMobileViewport = useMediaQuery(
    rootTheme.breakpoints.between('xxs', 'sm')
  );

  const [searchText, setSearchText] = useState('');
  const deferredSearchText = useDeferredValue(searchText);
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value),
    []
  );
  const displayedOptions = useMemo(() => {
    return props.multiple
      ? props.options.filter(option =>
          containsText(option.label.toString(), deferredSearchText)
        )
      : props.options;
  }, [deferredSearchText, props.options, props.multiple]);

  const selectedSet = useMemo(
    () => new Set(Array.isArray(SelectValue) ? SelectValue : [SelectValue]),
    [SelectValue]
  );

  // `menuOpen` is optional - when the consumer does not control the menu we
  // cannot tell when it opens, so expand once on mount and behave as before.
  const isMenuControlled = props.menuOpen !== undefined;
  const [optionLimit, setOptionLimit] = useState(OPTION_CHUNK);

  useEffect(() => {
    if (isMenuControlled && !props.menuOpen) {
      setOptionLimit(OPTION_CHUNK);
      return;
    }
    // A transition keeps the tail interruptible - if the user taps or types
    // while the remaining options render, React abandons that work.
    return scheduleIdle(() =>
      startTransition(() => setOptionLimit(Number.MAX_SAFE_INTEGER))
    );
  }, [isMenuControlled, props.menuOpen]);

  const visibleOptions = useMemo(
    () =>
      optionLimit >= displayedOptions.length
        ? displayedOptions
        : displayedOptions.slice(0, optionLimit),
    [displayedOptions, optionLimit]
  );

  const optionVars = useMemo(
    () =>
      ({
        '--lotus-opt-fs': props.hirist ? '16px' : '12px',
        '--lotus-opt-color': props.hirist ? '#555' : '#333',
        '--lotus-opt-my': props.hirist ? '4px' : '0px',
        '--lotus-opt-hover': props.hirist
          ? props.multiple
            ? 'transparent'
            : '#eff3fa'
          : props.hiristColor
            ? 'rgba(233, 99, 12, 0.08)'
            : !isAppIimjobs
              ? props.multiple
                ? 'transparent'
                : '#eff3fa'
              : '#91B87929',
      }) as any,
    [props.hirist, props.multiple, props.hiristColor]
  );

  const searchFieldSx = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: props.hirist ? '1px solid #7E7E7E33 !important' : 'none',
      },
      '.MuiInputBase-input': {
        border: 'unset !important',
        fontSize: props.hirist ? '14px !important' : '10px !important',
        paddingLeft: 0,
        backgroundColor: props.hirist ? '#F2F5FA' : 'transparent',
        paddingTop: props.hirist ? '8px !important' : '0px',
        paddingBottom: props.hirist ? '8px !important' : '0px',
      },
      '.MuiInputBase-root': {
        borderRadius: props.hirist ? '4px !important' : '100px',
        backgroundColor: props.hirist ? '#F2F5FA' : 'transparent',
        paddingTop: props.hirist ? '-8px !important' : '8px',
        paddingBottom: props.hirist ? '-8px !important' : '8px',
        border: props.hirist ? 'none' : '1px solid #7E7E7E33',
      },
    }),
    [props.hirist]
  );

  const isNarrowViewport = useMediaQuery('(max-width: 500px)');

  const roundedInputSx = useMemo(
    () => ({
      '& .MuiInputBase-input': {
        fontSize: !props.hirist ? 14 : 17,
        color: props.hirist ? 'rgba(0,0,0,0.4)' : '#333',
        border:
          props.hirist && !isNarrowViewport ? '0px' : '1px solid #7E7E7E33',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '30%', // Start at 25% from the top
          height: '40%', // Cover 50% of the height
          width: '1px', // Border thickness
          backgroundColor: '#7E7E7E33', // Border color,
          display: props.hirist && !isNarrowViewport ? 'block' : 'none',
        },
        borderRadius: props.hirist
          ? {xxs: '8px !important', md: '0px !important'}
          : '100px',
        pt: props.hirist ? '16px !important' : '8px',
        pb: props.hirist ? '16px !important' : '8px',
        '&:focus': {
          border: props.hirist ? {xxs: '1px solid #7E7E7E33', md: 'none'} : '',
          borderRadius: props.hirist
            ? {xxs: '8px !important', md: '0px !important'}
            : '100px',
        },
        '&:hover': {
          border: props.hirist ? {xxs: '1px solid #7E7E7E33', md: 'none'} : '',
          borderRadius: props.hirist
            ? {xxs: '8px !important', md: '0px !important'}
            : '100px',
        },
      },
    }),
    [props.hirist, isNarrowViewport]
  );

  const searchAdornment = useMemo(
    () => ({
      startAdornment: (
        <InputAdornment position="start">
          <Image src={SearchIcon} alt="search" width={12} height={12} />
        </InputAdornment>
      ),
    }),
    []
  );

  const closeMenu = () => {
    if (props.setMenuOpen) props.setMenuOpen(false);
    setTimeout(() => {
      setSearchText('');
    }, 100);
  };
  const handleChange = (event: any) => {
    let {
      target: {value},
    } = event;
    const newValue = handleValue(value);
    props.onChange && props.onChange(event, newValue);
  };
  const [viewportHeight, setViewportHeight] = useState<number>(0);

  useEffect(() => {
    const updateHeight = () => setViewportHeight(window.innerHeight);
    updateHeight();

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);
  const renderValue = (selected: any) => {
    if (!selected || (typeof selected === 'object' && !selected.length))
      return props.label;

    if (props.multiple) {
      return `${selected.length ?? SelectValue.length} Selected`;
    } else {
      const selectedObjects = props.options.filter(
        option => selected === option?.value
      );
      // return selectedObjects.map(option => option.label).join(', ');
      const selectedLabel = selectedObjects
        .map(option => option.label)
        .join(', ');
      if (props.truncatelength && isMobileViewport) {
        const truncatedText =
          selectedLabel.length > props.truncatelength
            ? `${selectedLabel.substring(0, props.truncatelength)}...`
            : selectedLabel;

        return (
          <Typography
            display={'flex'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            gap={1}
            sx={{
              color: '#333',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '18px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {truncatedText}
          </Typography>
        );
      }
      return selectedLabel;
    }
  };

  const handleValue = (optionValue: string | number) => {
    const option: OptionType | undefined = props.options.find(
      option => option?.value === optionValue
    );
    if (!option) {
      return false;
    }
    const value = option?.value ?? option.id ?? option;
    const isAlreadySelected = SelectValue?.indexOf(value) > -1;
    if (!isAlreadySelected) {
      if (props.multiple) {
        let finalList = [value];
        if (option.selected) {
          finalList = finalList.concat(option.selected);
        }

        return SelectValue.concat(finalList);
      } else {
        return [value];
      }
    } else {
      const filteredOptions = SelectValue.filter((_value: never) => {
        if (_value === value) {
          return false;
        }
        if (option?.selected?.length) {
          return option.selected?.indexOf(_value) === -1;
        }
        return true;
      });

      return filteredOptions;
    }
  };
  const handleOnClose = () => {
    if (!props.multiple) {
      closeMenu();
    }
    setTimeout(() => {
      setSearchText('');
    }, 100);
  };

  // MUI IS PLANNING TO MAKE SELECT A NON MODAL SELECT UNTIL THEN WE HAVE TO USE PATCH FIXES FOR SCROLL LOCK ON BODY
  // https://github.com/mui/material-ui/issues/17353#issuecomment-1703289570
  // This was asked to be removed by Design team (Arun), dated 11-12-2024.
  // useEffect(() => {
  //   const handler = () => {
  //     props.setMenuOpen && props.setMenuOpen(false);
  //   };
  //   window?.addEventListener('scroll', handler);
  //   return () => {
  //     window.removeEventListener('scroll', handler);
  //   };
  // }, []);
  const baseMenuSx = useMemo(
    () => ({
      maxHeight: props.hirist
        ? {xxs: 300, md: 550}
        : viewportHeight < 790 &&
            (props?.page === 'alumni' || props?.page === 'women_at_work')
          ? props?.page === 'alumni'
            ? 300
            : 250
          : 400,
      minWidth: props.menuMinWidth ?? {
        xxs: 200,
        md: isAppIimjobs ? 400 : 200,
        lg: isAppIimjobs ? 500 : 200,
      },
      paddingTop: 0,
      '.MuiMenu-list': {
        paddingTop: props.multiple ? 0 : 1,
        paddingBottom: props.multiple ? 0 : 1,
      },
      boxShadow: 'none !important',
      '.MuiPaper-elevation': {
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        marginTop: props.hirist ? {xxs: 1, md: 1} : 0.7,
        ...(props?.hirist ? {minHeight: '320px'} : {}),
      },
      zIndex: {xxs: 3, md: 1},
      cursor: 'default',
      bgcolor: {xxs: 'unset', md: 'unset'},
      backdropFilter: {xxs: 'unset', md: 'unset'},
      ul: {py: '0px'},
      ...optionVars,
    }),
    [
      props.hirist,
      props.page,
      props.menuMinWidth,
      props.multiple,
      viewportHeight,
      optionVars,
    ]
  );

  const menuSx = useMemo(
    () => ({
      ...(baseMenuSx as object),
      ...((props.MenuProps?.sx as object) ?? {}),
    }),
    [baseMenuSx, props.MenuProps?.sx]
  );

  const key = props.label?.toString().replace(' ', '-');
  const LabelComp = [
    VersionVariants.VERSION_TWO,
    VersionVariants.VERSION_HIRIST,
  ].includes(props.variant)
    ? InputLabel
    : MuiInputLabel;

  return (
    <FormControl
      size="small"
      sx={
        props?.maxWidth
          ? {width: '100%', maxWidth: props.maxWidth}
          : {width: '100%'}
      }
    >
      {/* <LabelComp id={`lotus-selectlabel-${key}`}>{props.label}</LabelComp> */}
      <Select
        labelId={`lotus-selectlabel-${key}`}
        id={`lotus-select-${props.name}`}
        data-name={props.name}
        // label={props.label}
        displayEmpty
        ref={ref}
        sx={props.sx ? {...props.sx} : {}}
        value={
          SelectValue.length
            ? props.multiple
              ? 9999
              : SelectValue[0]
            : SelectValue
        }
        open={props.menuOpen}
        inputProps={{
          'data-name': props.name,
          name: props.name,
          sx: {
            zIndex: 0,
          },
        }}
        onClose={handleOnClose}
        onOpen={() => props.setMenuOpen && props.setMenuOpen(props.name)}
        MenuProps={{
          onClose: closeMenu,
          disableScrollLock: true,
          // marginThreshold: null,
          // disablePortal: true,
          transitionDuration: 0,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          autoFocus: props.multiple ? false : true,
          ...props.MenuProps,
          sx: menuSx,
        }}
        onChange={handleChange}
        renderValue={renderValue}
        input={
          [
            VersionVariants.VERSION_TWO,
            VersionVariants.VERSION_HIRIST,
          ].includes(props.variant) ? (
            <RoundedInput
              sx={roundedInputSx}
              name={props.name}
              placeholder={SelectValue.length ? undefined : props.placeholder}
            />
          ) : undefined
        }
        IconComponent={
          [
            VersionVariants.VERSION_TWO,
            VersionVariants.VERSION_HIRIST,
          ].includes(props.variant)
            ? SelectIcon
            : undefined
        }
        name={props.name}
      >
        {props.multiple && (
          <ListSubheader
            sx={{lineHeight: '10px', paddingTop: 1.5, paddingBottom: 1.5}}
          >
            <TextField
              // size="small"
              // Autofocus on textfield
              autoFocus
              placeholder={
                props.hirist
                  ? `Search job ${props.label} here...`
                  : `Search ${props.label}`
              }
              fullWidth
              InputProps={searchAdornment}
              sx={searchFieldSx}
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={e => {
                if (e.key !== 'Escape') {
                  // Prevents autoselecting item while typing (default Select behaviour)
                  e.stopPropagation();
                }
              }}
              variant={VersionVariants.VERSION_TWO}
            />
          </ListSubheader>
        )}
        {visibleOptions?.length ? (
          visibleOptions.map((option: any, i: number) => {
            const isSelected = selectedSet.has(option?.value);
            return (
              <OptionItem
                key={`${props.name ?? i}-${i}-${option?.value}`}
                value={option?.value}
                selected={isSelected}
              >
                <OptionLabel>
                  {props.multiple && <Checkbox checked={isSelected} />}
                  {option.label}
                </OptionLabel>
              </OptionItem>
            );
          })
        ) : (
          <MenuItem
            key={`no-results-found`}
            disabled={true}
            sx={{
              fontSize: 12,
              ':hover, &.Mui-selected': {
                backgroundColor: !isAppIimjobs ? 'transparent' : '#91B87929',
              },
              '&.Mui-selected': {
                '*': {
                  fontWeight: 500,
                },
              },
            }}
          >
            {typeof props.label == 'string'
              ? `No ${props.label.toLowerCase()} found`
              : 'No results found'}
          </MenuItem>
        )}
        {props.hirist && props.multiple ? (
          <ListSubheader
            sx={{
              position: 'sticky',
              bottom: 0,
              borderTop: '1px solid #eee', // Optional separator
              boxShadow: '0 3px 31px 0 rgba(0,0,0,0.09)',
              py: 1.5,
              paddingRight: 3.5,
            }}
          >
            <Typography
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                fontSize: 14,
                color: 'rgba(0,0,0,0.5)',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (props.onChange) {
                  const event = {
                    target: {
                      value: props.multiple ? [] : '',
                      name: props.name || '', // Ensure name exists if required
                    },
                  } as SelectChangeEvent<unknown>;

                  props.onChange(event, null);
                }
              }}
            >
              Reset
            </Typography>
          </ListSubheader>
        ) : (
          <></>
        )}
      </Select>
    </FormControl>
  );
};
